import { createContext, useContext } from 'react';
import { GroupContractDto } from 'api/contract';
import { GroupContractProcessResponse, GroupContractStepIdentifier } from '../types/enums';

export interface GroupContractProcessContextValue {
  groupContractId: string;
  /** Identifiant lisible du contrat (ex: "G-CONFIA-26-000123-1"). Null si pas encore généré. */
  contractIdentifier: string | null;
  process: GroupContractProcessResponse | null;
  /**
   * Détails du contrat, chargés une seule fois par le provider et partagés par toutes
   * les cards (header, CP, souscripteur, détail) — ne pas refetch localement.
   */
  contract: GroupContractDto | null;
  /** Vrai uniquement pendant le chargement initial. Les refresh suivants sont silencieux. */
  isLoading: boolean;
  error: string | null;
  /**
   * Vrai si le contrat est verrouillé (au moins un mandat SEPA signé OU contrat finalisé).
   * Les cards Souscripteur / Produits / Courtiers doivent désactiver toute action de modif.
   */
  isLocked: boolean;
  /**
   * Incrémenté à chaque refresh (action utilisateur ou notification SignalR du hub
   * collectif). Les données annexes non portées par le contexte (documents, statut de
   * signature, mandats) se rechargent en s'abonnant à cette valeur.
   */
  dataVersion: number;
  /** Recharge process + contrat en place, sans démonter l'arbre (pas de flash). */
  refresh: () => Promise<void>;
  /** Builds an absolute step route for the current routing context (admin or broker). */
  buildStepRoute: (step: GroupContractStepIdentifier) => string;
}

export const GroupContractProcessContext =
  createContext<GroupContractProcessContextValue | undefined>(undefined);

export const useGroupContractProcess = (): GroupContractProcessContextValue => {
  const ctx = useContext(GroupContractProcessContext);
  if (!ctx) {
    throw new Error(
      'useGroupContractProcess must be used inside a GroupContractProcessProvider',
    );
  }
  return ctx;
};
